// apps/lumen/src/features/calendar/CalendarConnectionBanner.tsx
import { cva } from 'class-variance-authority';
import { Flex, Text, Button, Icon, message } from '@nula/core-ui';
import { ApiError } from '@/lib/http/client';
import { openCalendarOAuth } from '@/lib/calendar/calendar-oauth';
import type { CalendarConnection } from '@/stores/use-calendar-store';
import styles from './calendar-connection-banner.module.css';

const banner = cva(styles.base);

interface CalendarConnectionBannerProps {
  connections: CalendarConnection[];
}

export function CalendarConnectionBanner({ connections }: CalendarConnectionBannerProps) {
  const staleConnections = connections.filter(c => c.needsReauth);
  if (staleConnections.length === 0) return null;

  const handleReconnect = async (provider: 'google' | 'microsoft') => {
    try {
      await openCalendarOAuth(provider);
    } catch (err) {
      console.error('[CalendarConnectionBanner] openCalendarOAuth failed', err);
      const detail =
        err instanceof ApiError
          ? `${err.code} (${err.status})`
          : err instanceof Error
            ? err.message
            : String(err);
      message.error(`Couldn't open calendar OAuth: ${detail}`);
    }
  };

  return (
    <Flex
      align="center"
      gap="md"
      className={banner()}
    >
      <Icon name="warning" size={16} className={styles.icon} />
      <Text size="sm" className={styles.text}>
        {staleConnections.map(c => c.email).join(', ')} — calendar access expired.
      </Text>
      {staleConnections.map(c => (
        <Button
          key={c.id}
          size="sm"
          variant="default"
          onClick={() => handleReconnect(c.provider as 'google' | 'microsoft')}
        >
          Reconnect
        </Button>
      ))}
    </Flex>
  );
}
